import React from "react";

import { Button } from "@portex-pro/ui-components";
import { ShipperAddress } from "api/rest/address/types";
import { Accessorial, AccessorialType } from "api/types/generated-types";
import StepCollapse from "app/pages/quote-request/_components/StepCollapse";
import { useCreateStepCollapseProps } from "app/pages/quote-request/_hooks/useStepCollapse";
import { useQuoteRequestSelector } from "app/pages/quote-request/_store";
import { formatLocationShort } from "app/pages/quote-request/ftl/_utils";
import { QuoteStepIndex } from "app/pages/quote-request/types";
import { useFindAccessorialsQuery } from "pages/shipper/pages/request-quote/pages/ltl/api/generated/findAccessorials.generated";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { quoteRequestActions } from "../../../_store/quoteRequestSliceV2";
import { FinalSchema } from "../../_schemas";
import { LocationStopSection } from "../location/LocationStopSection";

type AddressLike = {
  name?: string | null;
  city?: string | null;
  province_name?: string | null;
  country_code?: string | null;
};

const LocationStepTag: React.FC<{ pickupAddress?: AddressLike; deliveryAddress?: AddressLike }> = ({
  pickupAddress,
  deliveryAddress,
}) => {
  const { t } = useTranslation("quoteRequest");
  if (!pickupAddress?.city && !deliveryAddress?.city) {
    return <></>;
  }

  const pickupLocation = formatLocationShort(pickupAddress as ShipperAddress);
  const deliveryLocation = formatLocationShort(deliveryAddress as ShipperAddress);

  return (
    <div className="inline-flex items-center bg-blue-50 rounded-md px-2 py-1">
      <span className="text-blue-600 font-bold mr-2 text-sm">
        {t("LocationStep.pickupToDelivery", {
          pickup: pickupLocation || t("LocationStep.pickup"),
          delivery: deliveryLocation || t("LocationStep.delivery"),
        })}
      </span>
    </div>
  );
};

export const LocationStep: React.FC<{ onSubmit: (data: FinalSchema) => void }> = ({ onSubmit }) => {
  const { t } = useTranslation(["quoteRequest", "shipper"]);
  const { stepIndex } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const setStepIndex = quoteRequestActions.useSetStepIndex();
  const { data } = useFindAccessorialsQuery();
  const accessorials = data?.findAccessorials.items;
  const pickupAccessorials = accessorials?.filter(
    (accessorial) => accessorial?.type === AccessorialType.Pickup
  ) as Accessorial[];
  const deliveryAccessorials = accessorials?.filter(
    (accessorial) => accessorial?.type === AccessorialType.Delivery
  ) as Accessorial[];

  const methods = useFormContext<FinalSchema>();

  const pickupAddress = useWatch({ name: "pickup.address", control: methods.control });
  const deliveryAddress = useWatch({ name: "delivery.address", control: methods.control });

  const { baseStepCollapseProps, setIsOpen } = useCreateStepCollapseProps({
    expectedStepIndex: QuoteStepIndex.Location,
    name: t("shipper:location"),
    number: 1,
  });

  const submit = (data: FinalSchema) => {
    onSubmit(data);
    setStepIndex(QuoteStepIndex.Shipment);
    setIsOpen(false);
  };

  return (
    <StepCollapse
      {...baseStepCollapseProps}
      tag={<LocationStepTag pickupAddress={pickupAddress} deliveryAddress={deliveryAddress} />}
      footer={
        stepIndex <= QuoteStepIndex.Location ? (
          <div className="flex justify-between mt-2 w-full">
            <div />

            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={methods.handleSubmit(submit)}
              style={{ width: 150 }}
            >
              {t("quoteRequest:LocationStep.nextButton")}
            </Button>
          </div>
        ) : null
      }
    >
      <div className="my-6">
        <LocationStopSection
          title={t("quoteRequest:LocationStep.pickup")}
          formKeyPrefix="pickup"
          accessorials={pickupAccessorials || []}
          defaultShowDateTime={true}
        />
        <LocationStopSection
          title={t("quoteRequest:LocationStep.delivery")}
          formKeyPrefix="delivery"
          accessorials={deliveryAccessorials || []}
        />
      </div>
    </StepCollapse>
  );
};
